'use client';

import {
  CANONICAL_WHATSAPP_TEMPLATE,
  fillWhatsappTemplate,
} from '@borradh-workspace/api-client';
import { CheckCheckIcon } from 'lucide-react';
import type { SampleRecipient } from '../../api/types';
import { mergeResolve } from './merge-resolve';

export interface WhatsAppPreviewProps {
  /** The user-authored message that fills the template's editable field. */
  message: string;
  /** The recipient whose first name fills `{{1}}` (optional → generic sample). */
  recipient?: SampleRecipient;
  /** The sending org's name, auto-bound to `{{2}}` — falls back to a sample. */
  businessName?: string;
  /**
   * The body that will ACTUALLY be sent — the org's approved template as
   * mirrored from Meta, not the canonical constant. They diverge: an org that
   * was provisioned before the canonical body gained its `{{2}}` business-name
   * slot still holds the approved two-param version, and `ensureCampaign-
   * WhatsappTemplate` is a name-keyed no-op, so it keeps sending that. Pinning
   * the preview to the constant would show those orgs a business-name sentence
   * their template does not contain — the one thing this pane exists to get
   * right. Defaults to the canonical body when no template has resolved yet.
   */
  templateBody?: string;
  renderedBody?: string;
  /**
   * Which `{{n}}` the business name binds to, or null when the resolved
   * template has no such slot (the legacy body, or a hand-created one whose
   * `{{2}}` means something else entirely).
   */
  businessNameParamIndex?: number | null;
  /** Which `{{n}}` the authored message binds to. */
  messageParamIndex?: number;
}

/**
 * Render a WhatsApp-styled chat bubble showing exactly what the recipient will
 * receive: the org's approved `borradh_campaign_message` template with `{{1}}`
 * bound to the first name, the business-name slot (when the template has one)
 * bound to the sending org, and the editable slot bound to the authored
 * message. The mandatory `Reply STOP to unsubscribe.` line is part of the
 * template body, so it is always shown — it can never be edited away (a hard
 * compliance gate).
 */
export function WhatsAppPreview({
  message,
  renderedBody,
  recipient,
  businessName,
  templateBody = CANONICAL_WHATSAPP_TEMPLATE.body,
  businessNameParamIndex = 2,
  messageParamIndex = 3,
}: WhatsAppPreviewProps) {
  const firstName = mergeResolve('{{firstName|there}}', recipient);
  const filled =
    renderedBody != null
      ? mergeResolve(renderedBody, recipient)
      : fillWhatsappTemplate(templateBody, {
          1: firstName,
          ...(businessNameParamIndex != null
            ? { [businessNameParamIndex]: businessName || 'our team' }
            : {}),
          [messageParamIndex]: mergeResolve(message, recipient),
        });

  const time = new Date().toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div
      className="rounded-lg border p-4 shadow-sm"
      style={{ backgroundColor: '#e5ddd5' }}
    >
      <div className="flex justify-end">
        <div
          className="relative max-w-[85%] rounded-lg px-3 py-2 text-sm text-[#111b21] shadow-sm"
          style={{ backgroundColor: '#d9fdd3' }}
        >
          <p className="whitespace-pre-wrap break-words leading-relaxed">
            {filled}
          </p>
          <div className="mt-1 flex items-center justify-end gap-1 text-[#667781] text-[11px]">
            <span>{time}</span>
            <CheckCheckIcon className="size-3.5 text-[#53bdeb]" />
          </div>
        </div>
      </div>
    </div>
  );
}
